/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LoginDesignTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the branding module.
----------------------------------------------------------------*/

import { LogIn, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SimpleTooltip from '@/components/ui/SimpleTooltip';
import Toggle from '@/components/ui/Toggle';
import { ColorPickerWithAlpha } from '@/components/ui/ColorPickerWithAlpha';
import { useI18n } from '@/hooks/useI18n';
import { BrandingImageField, TabProps } from './types';

export const LoginDesignTab: React.FC<TabProps> = ({
  formData,
  onInputChange,
  onFileUpload,
  onRemoveFile,
  previews,
  uploadingField
}) => {
  const { t: safeT } = useI18n();

  const triggerFileInput = (id: string) => {
    const input = document.getElementById(id) as HTMLInputElement | null;
    input?.click();
  };

  const renderFileButtons = (field: BrandingImageField, inputId: string, emptyLabelKey: string) => (
    <div className="flex justify-center gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={() => triggerFileInput(inputId)}
        className="text-xs px-2 py-1 h-7"
        disabled={uploadingField === field}
      >
        <Upload className="mr-1 h-3 w-3" />
        {previews[field] || formData[field]
          ? safeT('branding.actions.change', 'Change')
          : safeT(emptyLabelKey, 'Upload')}
      </Button>
      {(previews[field] || formData[field]) && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRemoveFile(field)}
          className="text-xs px-2 py-1 h-7"
        >
          <X className="mr-1 h-3 w-3" />
          {safeT('branding.actions.remove', 'Remove')}
        </Button>
      )}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            onFileUpload(field, file);
            event.target.value = '';
          }
        }}
      />
    </div>
  );

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-6 flex items-center gap-2 text-lg font-semibold text-foreground">
        <LogIn className="h-5 w-5" />
        {safeT('branding.loginDesign', 'Login Design')}
      </h3>

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 items-center gap-4">
          <SimpleTooltip content={safeT('branding.tooltips.loginBackgroundColor')} maxWidth="300px">
            <label className="text-sm font-medium text-foreground">
              {safeT('branding.labels.loginBackgroundColor', 'Background Color')}
            </label>
          </SimpleTooltip>
          <ColorPickerWithAlpha
            value={formData.loginBackgroundColor || '#ffffff'}
            onChange={(color) => onInputChange('loginBackgroundColor', color)}
            placeholder="#ffffff"
          />

          <SimpleTooltip content={safeT('branding.tooltips.loginFontColor')} maxWidth="300px">
            <label className="text-sm font-medium text-foreground">
              {safeT('branding.labels.loginFontColor', 'Font Color')}
            </label>
          </SimpleTooltip>
          <ColorPickerWithAlpha
            value={formData.loginFontColor || '#ffffff'}
            onChange={(color) => onInputChange('loginFontColor', color)}
            placeholder="#ffffff"
          />

          <SimpleTooltip content={safeT('branding.tooltips.loginFormBackground')} maxWidth="300px">
            <label className="text-sm font-medium text-foreground">
              {safeT('branding.labels.loginFormBackground', 'Form Background')}
            </label>
          </SimpleTooltip>
          <ColorPickerWithAlpha
            value={formData.loginFormBackground || '#444847'}
            onChange={(color) => onInputChange('loginFormBackground', color)}
            placeholder="#444847"
          />

          <SimpleTooltip content={safeT('branding.tooltips.loginSubmitButtonColor')} maxWidth="300px">
            <label className="text-sm font-medium text-foreground">
              {safeT('branding.labels.loginSubmitButtonColor', 'Submit Button Color')}
            </label>
          </SimpleTooltip>
          <ColorPickerWithAlpha
            value={formData.loginSubmitButtonColor || '#202c2c'}
            onChange={(color) => onInputChange('loginSubmitButtonColor', color)}
            placeholder="#202c2c"
          />

          <SimpleTooltip content={safeT('branding.tooltips.loginAddonsMenuColor')} maxWidth="300px">
            <label className="text-sm font-medium text-foreground">
              {safeT('branding.labels.loginAddonsMenuColor', 'Add-ons Menu Color')}
            </label>
          </SimpleTooltip>
          <ColorPickerWithAlpha
            value={formData.loginAddonsMenuColor || '#243636'}
            onChange={(color) => onInputChange('loginAddonsMenuColor', color)}
            placeholder="#243636"
          />

          <SimpleTooltip content={safeT('branding.tooltips.disableLoginBackgroundImage')} maxWidth="300px">
            <label className="text-sm font-medium text-foreground">
              {safeT('branding.labels.disableLoginBackgroundImage', 'Disable Background Image')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.disableLoginBackgroundImage || false}
              onChange={(value) => onInputChange('disableLoginBackgroundImage', value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <h4 className="text-md font-medium text-foreground">
              {safeT('branding.labels.loginBackgroundImage', 'Background Image')}
            </h4>
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              {previews.loginBackgroundImage || formData.loginBackgroundImage ? (
                <div className="space-y-2">
                  <img
                    src={previews.loginBackgroundImage || formData.loginBackgroundImage || ''}
                    alt={safeT('branding.alt.loginBackgroundImagePreview', 'Login background preview') || 'Login background preview'}
                    className="h-24 w-full rounded object-cover"
                  />
                  {renderFileButtons('loginBackgroundImage', 'branding-login-background-upload', 'branding.actions.upload')}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <Upload className="mb-1 h-6 w-6 text-muted-foreground" />
                  <p className="mb-2 text-xs text-muted-foreground">
                    {safeT('branding.emptyStates.noBackgroundImage', 'No background image')}
                  </p>
                  {renderFileButtons('loginBackgroundImage', 'branding-login-background-upload', 'branding.actions.selectImage')}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-md font-medium text-foreground">
              {safeT('branding.labels.loginLogo', 'Logo')}
            </h4>
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              {previews.loginLogo || formData.loginLogo ? (
                <div className="space-y-2">
                  <img
                    src={previews.loginLogo || formData.loginLogo || ''}
                    alt={safeT('branding.alt.loginLogoPreview', 'Login logo preview') || 'Login logo preview'}
                    className="mx-auto h-16 w-auto object-contain"
                  />
                  {renderFileButtons('loginLogo', 'branding-login-logo-upload', 'branding.actions.upload')}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <Upload className="mb-1 h-6 w-6 text-muted-foreground" />
                  <p className="mb-2 text-xs text-muted-foreground">
                    {safeT('branding.emptyStates.noLogo', 'No logo uploaded')}
                  </p>
                  {renderFileButtons('loginLogo', 'branding-login-logo-upload', 'branding.actions.selectImage')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
